import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calculator, ExternalLink, Info, CheckCircle2, Clock, Check } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useNavigation } from "@/components/layout/app-layout";
import { StockItem } from "@/types/stock-item";

interface StockItemCostingInfoProps {
  item: StockItem;
}

export function StockItemCostingInfo({ item }: StockItemCostingInfoProps) {
  const { navigateTo } = useNavigation();

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 2,
      maximumFractionDigits: 4, // Show more precision for average cost
    }).format(amount);
  };

  // Format percentage
  const formatPercentage = (value: number): string => {
    return `${value.toFixed(2)}%`;
  };

  // Calculate margin
  const margin = item.unitPrice > 0 
    ? ((item.unitPrice - item.averageCost) / item.unitPrice) * 100 
    : 0;

  // Calculate markup
  const markup = item.averageCost > 0 
    ? ((item.unitPrice - item.averageCost) / item.averageCost) * 100 
    : 0;

  // Get margin color
  const getMarginColor = (percentage: number): string => {
    if (percentage > 30) return "text-green-600";
    if (percentage >= 15) return "text-orange-600";
    return "text-red-600";
  };

  // Calculate inventory value at cost
  const inventoryValueAtCost = item.physicalQuantity * item.averageCost;

  // Calculate potential profit (if all sold at current price)
  const potentialProfit = (item.physicalQuantity * item.unitPrice) - inventoryValueAtCost;

  return (
    <Card className="border-2 border-primary/20 bg-primary/5">
      <CardHeader>
        <div className="space-y-1">
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-primary" />
            Valuation Method
          </CardTitle>
          <CardDescription>
            How this item's cost is calculated
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Active Method */}
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Badge variant="default" className="gap-1.5 px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700">
              <CheckCircle2 className="h-4 w-4" />
              Average Cost
            </Badge>
            <Badge variant="outline" className="gap-1.5 px-2 py-1 text-xs">
              ✓ Implemented
            </Badge>
          </div>
          
          <div className="text-sm text-muted-foreground space-y-1">
            <p>
              <strong>Weighted Average Cost</strong> method is currently active for all stock items.
            </p>
            <p className="text-xs">
              Cost is recalculated automatically with each purchase, providing a balanced view of inventory value.
            </p>
          </div>
        </div>

        {/* Current Cost Values */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
          {/* Average Cost */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Average Cost
              </span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">
                      Calculated as: (Total Value of Inventory) ÷ (Total Quantity)
                    </p>
                    <p className="text-xs mt-1">
                      Updates automatically with each purchase receipt
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="text-2xl font-bold text-primary">
              {formatCurrency(item.averageCost)}
            </div>
            <p className="text-xs text-muted-foreground">
              Per unit
            </p>
          </div>

          {/* Last Cost Price */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Last Cost Price
              </span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">
                      The cost from the most recent purchase
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="text-2xl font-bold text-muted-foreground">
              {formatCurrency(item.lastCostPrice)}
            </div>
            <p className="text-xs text-muted-foreground">
              Most recent
            </p>
          </div>

          {/* Inventory Value */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Inventory Value
              </span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">
                      Total value of stock on hand at average cost
                    </p>
                    <p className="text-xs mt-1">
                      {item.physicalQuantity} units × {formatCurrency(item.averageCost)}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="text-2xl font-bold text-primary">
              {formatCurrency(inventoryValueAtCost)}
            </div>
            <p className="text-xs text-muted-foreground">
              At cost
            </p>
          </div>
        </div>

        {/* Profitability Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
          {/* Margin */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Margin
              </span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">
                      (Unit Price - Average Cost) ÷ Unit Price × 100
                    </p>
                    <p className="text-xs mt-1">
                      ({formatCurrency(item.unitPrice)} - {formatCurrency(item.averageCost)}) ÷ {formatCurrency(item.unitPrice)} × 100
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className={`text-2xl font-bold ${getMarginColor(margin)}`}>
              {formatPercentage(margin)}
            </div>
            <p className="text-xs text-muted-foreground">
              Profit margin
            </p>
          </div>

          {/* Markup */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Markup
              </span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">
                      (Unit Price - Average Cost) ÷ Average Cost × 100
                    </p>
                    <p className="text-xs mt-1">
                      ({formatCurrency(item.unitPrice)} - {formatCurrency(item.averageCost)}) ÷ {formatCurrency(item.averageCost)} × 100
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className={`text-2xl font-bold ${getMarginColor(margin)}`}>
              {formatPercentage(markup)}
            </div>
            <p className="text-xs text-muted-foreground">
              Cost markup
            </p>
          </div>

          {/* Potential Profit */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Potential Profit
              </span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">
                      If all {item.physicalQuantity} units sold at {formatCurrency(item.unitPrice)}
                    </p>
                    <p className="text-xs mt-1">
                      Revenue: {formatCurrency(item.physicalQuantity * item.unitPrice)}
                    </p>
                    <p className="text-xs">
                      Cost: {formatCurrency(inventoryValueAtCost)}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className={`text-2xl font-bold ${potentialProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(potentialProfit)}
            </div>
            <p className="text-xs text-muted-foreground">
              If all sold
            </p>
          </div>
        </div>

        {/* How It Works */}
        <div className="pt-4 border-t space-y-3">
          <h4 className="text-sm font-semibold flex items-center gap-2">
            <Calculator className="h-4 w-4" />
            How Average Cost Works
          </h4>
          <div className="space-y-2 text-xs text-muted-foreground">
            <div className="flex gap-2">
              <span className="font-mono bg-muted px-2 py-0.5 rounded">1</span>
              <p>
                <strong>On Purchase:</strong> New average = (Current Value + New Purchase Value) ÷ (Current Qty + New Qty)
              </p>
            </div>
            <div className="flex gap-2">
              <span className="font-mono bg-muted px-2 py-0.5 rounded">2</span>
              <p>
                <strong>On Sale:</strong> COGS = Average Cost × Quantity Sold
              </p>
            </div>
            <div className="flex gap-2">
              <span className="font-mono bg-muted px-2 py-0.5 rounded">3</span>
              <p>
                <strong>On Adjustment:</strong> Average cost remains unchanged (quantity adjusted only)
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
